import React from "react";
import { Breadcrumb } from "../../../atoms/breadcrumb";
import { Input } from "../../../atoms/input";
import { Table } from "../../../atoms/table";
import { Divider } from "../../../atoms/divider";
import { Dropdown } from "../../../atoms/dropdown";
import { Pagination } from "../../../molecules/pagination";
import { PAGE_NAME, ROUTE } from "../../../../constants/constants";
import { usePaginationHook } from "../../../molecules/pagination/usePaginationHook";
import { pascalToSnakeCase } from "../../../../utils";
import { useTableHooks } from "../../../atoms/table/useTableHooks";
import { useCloudBudgetListQuery, useCloudBudgetReportQuery, useCloudBudgetYearlyReportQuery } from "../../../../store/cost/costSlice";
import { CustomDatePicker } from "../../../atoms/datePicker";
import { Icon } from "../../../atoms/icon";
import { useNavigate } from "react-router-dom";
import { ColumnChart } from "../../../molecules/columnChart";
import { Loader } from "../../../atoms/loader";
import { TableChart } from "../../../molecules/tableChart";

const toolSortHandler = (sortBy: string) => {
    if (sortBy === "paymentPeriod") {
        sortBy = "startDate";
    } else if (sortBy === "annualCostJPY") {
        sortBy = "annualCost";
    } else if (sortBy === "monthlyCostJPY") {
        sortBy = "monthlyCost";
    } else if (sortBy === "userStatus") {
        sortBy = "status";
    }

    return sortBy;
};

const CloudBudgetList = () => {
    const pageClassName = "p-cloudBudgetList";
    const navigate = useNavigate();

    const filterRef = React.useRef<HTMLDivElement>(null);
    const [isShowFilter, setShowFilter] = React.useState(false);
    const [filter, setFilter] = React.useState({
        startDate: "",
        endDate: "",
    });

    const [filterQuery, setFilterQuery] = React.useState({
        startDate: "",
        endDate: "",
    });
    const [year, setYear] = React.useState({
        label: (new Date().getFullYear() - 1).toString() + "~" + new Date().getFullYear().toString(),
        value: new Date().getFullYear(),
    });
    const [filterYear, setFilterYear] = React.useState({
        label: (new Date().getFullYear() - 1).toString() + "~" + new Date().getFullYear().toString(),
        value: new Date().getFullYear(),
    });
    const tableReportFilterRef = React.useRef<HTMLDivElement>(null);
    const [isShowTableReportFilter, setShowTableReportFilter] = React.useState(false);


    const reportFilterRef = React.useRef<HTMLDivElement>(null);
    const [isShowReportFilter, setShowReportFilter] = React.useState(false);
    const [reportFilter, setReportFilter] = React.useState({
        tool_name: {
            label: "GCP",
            value: "GCP",
        },
        number_of_months: {
            label: "6 Months",
            value: 6,
        },
        type: {
            label: "Tool Wise",
            value: "tool_wise",
        },
    });

    const [reportQuery, setReportQuery] = React.useState({
        tool_name: reportFilter.tool_name.value,
        number_of_months: reportFilter.number_of_months.value,
        type: reportFilter.type.value,
    });

    const {
        search,
        query,
        pageChangeHandler,
        sortHandler,
        limitChangeHandler,
        searchHandler,
    } = useTableHooks();

    const { data, isLoading, isFetching } = useCloudBudgetListQuery({
        search: query.search,
        limit: query.limit,
        current_page: query.current,
        sort_by: pascalToSnakeCase(toolSortHandler(query.sortBy)),
        sort_direction: query.sortDirection,
        start_date: filterQuery.startDate,
        end_date: filterQuery.endDate
    });

    const { data: tableData, isSuccess: tableDataSuccess, isFetching: tableDataFetching } = useCloudBudgetYearlyReportQuery({
        year: filterYear.value,
    });


    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: true,
        },
        {
            title: "Name",
            dataIndex: "name",
            isSortable: true,
        },
        {
            title: "Tool Name",
            dataIndex: "saasTool",
            isSortable: true,
        },
        {
            title: "Period",
            dataIndex: "paymentPeriod",
            isSortable: true,
        },
        {
            title: "Monthly Cost",
            dataIndex: "monthlyCostJPY",
            isSortable: true,
        },
        {
            title: "Annual Cost",
            dataIndex: "annualCostJPY",
            isSortable: true,
        },
        {
            title: "File",
            dataIndex: "budgetFile",
            isSortable: false,
        }
    ];



    const {
        totalData,
        handleFirstPage,
        handlePrevPage,
        handleLastPage,
        handleNextPage,
        renderPageNumbers,
    } = usePaginationHook({
        totalNumberOfData: data?.result?.total,
        perPage: query.limit,
        setCurrentPage: pageChangeHandler,
        currentPage: query.current,
        className: "m-pagination",
    });

    const resetFilter = () => {
        setFilter({
            startDate: "",
            endDate: "",
        });

        handleFirstPage();
    };

    const resetReportFilter = () => {
        setReportFilter({
            tool_name: {
                label: "GCP",
                value: "GCP",
            },
            number_of_months: {
                label: "6 Months",
                value: 6,
            },
            type: {
                label: "Tool Wise",
                value: "tool_wise",
            },
        });
    };

    const reportFilterHandler = () => {
        setReportQuery({
            tool_name: reportFilter.tool_name.value,
            number_of_months: reportFilter.number_of_months.value,
            type: reportFilter.type.value,
        });
    }

    const filterHandler = () => {
        setFilterQuery({
            ...filterQuery,
            startDate: filter.startDate,
            endDate: filter.endDate,
        });
    };

    const { data: reportData, isLoading: reportLoading, isFetching: reportFetching, isSuccess: reportSuccess } = useCloudBudgetReportQuery({
        tool_name: reportQuery.tool_name,
        number_of_months: reportQuery.number_of_months,
        type: reportQuery.type,
    })

    const reportOptions: object = {
        title: `${(reportQuery.tool_name && reportQuery.type !== "all") ? reportQuery.tool_name : "Over All"} - Cloud Budget & Payment Report`,
        vAxis: { title: "Amount" },
        hAxis: { title: "Month" },
        tooltip: { isHtml: true },
        seriesType: "bars",
        backgroundColor: "#f5f5f5",
        // legend: { position: "none" },
        series: {
            0: { color: "green" },
            1: { color: "#fc5e03" },
            2: { color: "#910a0a" },
            3: { color: "blue" },
        },
    };




    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target as Node)
            ) {
                setShowFilter(false);
            }

            if (
                reportFilterRef.current &&
                !reportFilterRef.current.contains(event.target as Node)
            ) {
                setShowReportFilter(false);
            }

            if (
                tableReportFilterRef.current &&
                !tableReportFilterRef.current.contains(event.target as Node)
            ) {
                setShowTableReportFilter(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.CLOUD_BUDGET}</p>

            <div className={`${pageClassName}__chart`} ref={reportFilterRef}>
                <div className={`${pageClassName}__filter`} >
                    <button
                        onClick={() => setShowReportFilter(!isShowReportFilter)}
                        className={`${pageClassName}__filter__button`}
                        title="Filter"
                    >
                        <Icon icon="filter" size="primary" />
                    </button>

                    {isShowReportFilter && (
                        <div className={`${pageClassName}__filter__container`}>
                            <div className={`${pageClassName}__filter__item`}>
                                <label>Type</label>
                                <Dropdown
                                    options={[
                                        { label: "ALL", value: "all" },
                                        { label: "Tool Wise", value: "tool_wise" },
                                    ]}
                                    selected={reportFilter.type}
                                    onClickChange={(e) => {
                                        setReportFilter({ ...reportFilter, type: e as { label: string, value: string }, tool_name: { label: "GCP", value: "GCP" } });
                                    }}
                                    isBordered
                                    isIcon
                                />
                            </div>
                            {reportFilter.type.value === "tool_wise" && (
                                <div className={`${pageClassName}__filter__item`}>
                                    <label>Tool Name</label>
                                    <Dropdown
                                        options={[
                                            { label: "GCP", value: "GCP" },
                                            { label: "AWS", value: "AWS" },
                                            { label: "Azure", value: "Azure" },
                                            { label: "Namecheap", value: "Namecheap" },
                                        ]}
                                        selected={reportFilter.tool_name}
                                        onClickChange={(e) => {
                                            setReportFilter({ ...reportFilter, tool_name: e as { label: string, value: string } });
                                        }}
                                        isBordered
                                        isIcon
                                    />
                                </div>
                            )}
                            <div className={`${pageClassName}__filter__item`}>
                                <label>Number of Months</label>
                                <Dropdown
                                    options={[
                                        { label: "3 Months", value: 3 },
                                        { label: "6 Months", value: 6 },
                                        { label: "9 Months", value: 9 },
                                        { label: "12 Months", value: 12 },
                                    ]}
                                    selected={reportFilter.number_of_months}
                                    onClickChange={(e) => {
                                        setReportFilter({ ...reportFilter, number_of_months: e as { label: string, value: number } });
                                    }}
                                    isBordered
                                    isIcon
                                />
                            </div>
                            <Divider />
                            <div className={`${pageClassName}__filter__btn`}>
                                <button onClick={resetReportFilter}>Reset</button>
                                <button
                                    onClick={() => {
                                        setShowReportFilter(false);
                                        reportFilterHandler();
                                    }}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Loader isLoading={reportSuccess && reportFetching}>
                <ColumnChart
                    data={reportData?.result ? JSON.parse(JSON.stringify(reportData?.result)) : []}
                    options={reportOptions}
                    isLoading={reportLoading}
                />
            </Loader>



            <div className={`${pageClassName}__chartTable`}>
                <p className={`${pageClassName}__chartTable__title`}>Yearly Report ({filterYear.label})</p>
                <div className={`${pageClassName}__chart`} ref={tableReportFilterRef}>
                    <div className={`${pageClassName}__filter`} >
                        <button
                            onClick={() => setShowTableReportFilter(!isShowTableReportFilter)}
                            className={`${pageClassName}__filter__button`}
                            title="Filter"
                        >
                            <Icon icon="filter" size="primary" />
                        </button>

                        {isShowTableReportFilter && (
                            <div className={`${pageClassName}__filter__container`}>
                                <div className={`${pageClassName}__filter__item`}>
                                    <label>Year</label>
                                    <Dropdown
                                        options={tableData?.result?.years}
                                        selected={year}
                                        onClickChange={(e) => {
                                            setYear(e as { label: string, value: number });
                                        }}
                                        isBordered
                                        isIcon
                                    />
                                </div>

                                <Divider />
                                <div className={`${pageClassName}__filter__btn`}>
                                    <button onClick={() => {
                                        setFilterYear({
                                            label: (new Date().getFullYear() - 1).toString() + " ~ " + new Date().getFullYear().toString(),
                                            value: new Date().getFullYear() - 1,
                                        })
                                        setYear({
                                            label: (new Date().getFullYear() - 1).toString() + " ~ " + new Date().getFullYear().toString(),
                                            value: new Date().getFullYear() - 1,
                                        });
                                    }}>Reset</button>
                                    <button
                                        onClick={() => {
                                            setShowTableReportFilter(false);
                                            setFilterYear(year);
                                        }}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <Loader isLoading={tableDataSuccess && tableDataFetching}>
                    <TableChart data={tableData?.result?.data ?? []} />
                </Loader>
                <p className={`${pageClassName}__chartTable__note`}>*Note: Namecheap does not have regular billing. Payments are made as needed.</p>
            </div>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__header`}>
                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={(e) => {
                                searchHandler(e);
                                handleFirstPage();
                            }}
                            value={search}
                        />
                    </div>

                    <div className={`${pageClassName}__filterBy`}>
                        {(filterQuery.startDate || filterQuery.endDate) && (
                            <div className={`${pageClassName}__filterBy__item`}>
                                <p>
                                    {filterQuery.startDate} - {filterQuery.endDate}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className={`${pageClassName}__filter`} ref={filterRef}>
                        <button
                            onClick={() => navigate(ROUTE.CLOUD_BUDGET + ROUTE.ADD_BUDGET)}
                            className={`${pageClassName}__filter__button ${pageClassName}__filter__button--synchronize`}
                            title="Add New"
                        >
                            Add New
                        </button>

                        <button
                            onClick={() => setShowFilter(!isShowFilter)}
                            className={`${pageClassName}__filter__button`}
                            title="Filter"
                        >
                            <Icon icon="filter" size="primary" />
                        </button>

                        {isShowFilter && (
                            <div className={`${pageClassName}__filter__container`}>
                                <div className={`${pageClassName}__filter__item`}>
                                    <label>Start Date</label>
                                    <CustomDatePicker
                                        value={
                                            filter.startDate ? new Date(filter.startDate) : undefined
                                        }
                                        onChange={(date) =>
                                            setFilter({
                                                ...filter,
                                                startDate: date?.toString() || "",
                                            })
                                        }
                                    />
                                </div>
                                <div className={`${pageClassName}__filter__item`}>
                                    <label>End Date</label>
                                    <CustomDatePicker
                                        value={
                                            filter.endDate ? new Date(filter.endDate) : undefined
                                        }
                                        onChange={(date) => {
                                            setFilter({ ...filter, endDate: date?.toString() || "" });
                                        }}
                                    />
                                </div>
                                <Divider />
                                <div className={`${pageClassName}__filter__btn`}>
                                    <button onClick={resetFilter}>Reset</button>
                                    <button
                                        onClick={() => {
                                            setShowFilter(false);
                                            filterHandler();
                                            handleFirstPage();
                                        }}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${pageClassName}__table`}>
                    <Table
                        header={tableHeader}
                        data={data?.result?.data ?? []}
                        loading={isLoading || isFetching}
                        firstLoad={isLoading}
                        sortHandler={sortHandler}
                        minWidth="1440"
                        startIndex={data?.result?.from}
                        sortBy={query.sortBy}
                        viewURL={ROUTE.CLOUD_BUDGET}
                        cursorPointer
                    />
                </div>
                <Divider />

                <div className={`${pageClassName}__pagination`}>
                    <div className={`${pageClassName}__pagination__limit`}>
                        <p>Display</p>
                        <div className={`${pageClassName}__pagination__dropdown`}>
                            <Dropdown
                                options={[
                                    { label: "20", value: 20 },
                                    { label: "50", value: 50 },
                                    { label: "100", value: 100 },
                                ]}
                                selected={{ label: query.limit, value: query.limit }}
                                onClickChange={(e) => {
                                    limitChangeHandler(e.value as number);
                                    handleFirstPage();
                                }}
                                isBordered
                                isIcon

                            />
                        </div>
                    </div>
                    <div className={`${pageClassName}__pagination__number`}>
                        {data?.result?.total ? (
                            <p>
                                Show {data?.result?.from} -{" "}
                                {data?.result?.from + query.limit - 1 < data?.result?.total
                                    ? data?.result?.from + query.limit - 1
                                    : data?.result?.total}{" "}
                                of {data?.result?.total}
                            </p>
                        ) : (
                            <p>Show 00 - 00 of 00</p>
                        )}
                    </div>
                    <div className={`${pageClassName}__pagination__nav`}>
                        {data?.result?.total > query.limit && (
                            <Pagination
                                totalData={totalData}
                                page={query.current}
                                handleFirstPage={handleFirstPage}
                                handlePrevPage={handlePrevPage}
                                handleLastPage={handleLastPage}
                                handleNextPage={handleNextPage}
                                renderPageNumbers={renderPageNumbers}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};
export default CloudBudgetList;